import axios from "axios";
import { toast } from "react-hot-toast";
import store from "../store";
import { logout } from "../store/slices/auth-slice";

const api = axios.create({
  baseURL: process.env.REACT_APP_TERRORISM_SERVER,
  headers: { "X-Custom-Header": "foobar" },
});

const shownMessages = new Set();

const showToast = (type, message) => {
  if (shownMessages.has(message)) return;

  if (type === 'error') toast.error(message);
  else if (type === 'success') toast.success(message);

  shownMessages.add(message);

  setTimeout(() => {
    shownMessages.delete(message);
  }, 5000);
};

api.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem("token");
    const pathname = window.location.pathname;
    config.headers.originPath = pathname;
    config.headers.authorization = `Bearer ${accessToken}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track whether a token refresh is already in flight to avoid parallel refresh calls
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach((cb) => (error ? cb.reject(error) : cb.resolve(token)));
  refreshQueue = [];
};

const doLogout = () => {
  store.dispatch(logout());
  localStorage.clear();
};

api.interceptors.response.use(
  (response) => {
    const { data } = response;
    if (data.notify) {
      showToast('success', data.message ?? "Success");
    }
    return response.data;
  },
  async (error) => {
    const { response } = error;

    if (!response) {
      // Network error — no HTTP response received (CORS, connection refused, etc.)
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (response.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        showToast('error', 'Session expired. Please log in.');
        doLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the refresh resolves
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_TERRORISM_SERVER}auth/refresh-token`,
          { refreshToken }
        );
        const newToken = refreshResponse.data?.data?.token;
        localStorage.setItem("token", newToken);
        api.defaults.headers.common.authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        originalRequest.headers.authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        showToast('error', 'Session expired. Please log in.');
        doLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (response.data?.notify === true && response.status !== 401) {
      showToast('error', response?.data?.message);
    } else if (response.status !== 200 && response.status !== 401) {
      showToast('error', response?.data?.message || response?.data?.[0]);
    }

    return Promise.reject(error);
  }
);

export default api;
